import React, { useState } from 'react';

const XRThesisShowcase = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const conditions = [
    { name: 'Virtual Walls + Landmarks', accuracy: '71.8%', color: 'from-purple-500 to-blue-500' },
    { name: 'Virtual Walls + No Landmarks', accuracy: '81.8%', color: 'from-blue-500 to-cyan-500' },
    { name: 'Real Walls + Landmarks', accuracy: '78.9%', color: 'from-cyan-500 to-green-500' },
    { name: 'Real Walls + No Landmarks', accuracy: '89.3%', color: 'from-green-500 to-emerald-500' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 text-white">
      {/* Hero Section */}
      <header className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-96 h-96 bg-purple-500/20 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-blue-500/20 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <div className="inline-block mb-4 px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-full">
            <span className="text-purple-300 text-sm font-medium">Honours Research Thesis</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent leading-tight">
            Understanding Virtual to Real Content in Extended Reality
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-8">
            Investigating how virtual and real environmental elements affect spatial memory performance and user experience in XR environments
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a
              href="https://drive.google.com/file/d/1oszfhDJw5bdDRSy5_XFAuT1kl34LArov/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 rounded-lg transition-all font-semibold shadow-lg shadow-purple-500/30"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Read Full Thesis</span>
            </a>
          </div>
          
          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <div className="text-3xl font-bold text-purple-400">2,016</div>
              <div className="text-gray-400 text-sm">Total Trials</div>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <div className="text-3xl font-bold text-blue-400">8</div>
              <div className="text-gray-400 text-sm">Participants</div>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <div className="text-3xl font-bold text-cyan-400">89.3%</div>
              <div className="text-gray-400 text-sm">Best Accuracy</div>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <div className="text-3xl font-bold text-green-400">4</div>
              <div className="text-gray-400 text-sm">Conditions Tested</div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {['overview', 'methodology', 'findings', 'implications'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 rounded-lg font-medium transition-all capitalize ${
                activeTab === tab
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                  : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-12">
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-purple-400 mb-6">Research Question</h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                How do different combinations of virtual and real environmental elements affect spatial memory 
                performance and subjective user experience in extended reality environments?
              </p>
              <div className="bg-purple-900/20 border-l-4 border-purple-400 pl-6 py-4">
                <p className="text-purple-200 italic">
                  "The key finding challenges conventional assumptions about landmarks in VR—they may create 
                  cognitive interference rather than spatial support."
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
                <h3 className="text-xl font-bold text-blue-400 mb-4">The Problem</h3>
                <p className="text-gray-300 leading-relaxed">
                  As extended reality technologies become more prevalent, designers need guidance on how to 
                  create effective mixed environments. The question of whether virtual or real spatial references 
                  better support cognitive tasks remains largely unexplored.
                </p>
              </div>
              <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
                <h3 className="text-xl font-bold text-cyan-400 mb-4">The Approach</h3>
                <p className="text-gray-300 leading-relaxed">
                  Using a Meta Quest 3 HMD, participants completed spatial selection and memory tasks across 
                  four environmental conditions in a 2×2 factorial design comparing wall type (Virtual vs. Real) 
                  and landmark presence (Present vs. Absent).
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'methodology' && (
          <div className="space-y-12">
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-purple-400 mb-6">Experimental Design</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">2×2 Factorial Design</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      <span><strong>Factor 1:</strong> Wall Type (Virtual vs. Real)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      <span><strong>Factor 2:</strong> Landmarks (Present vs. Absent)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      <span>Within-subjects design with Latin square counterbalancing</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">Equipment & Setup</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Meta Quest 3 HMD with passthrough capabilities</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Unity-based experimental environment</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Millisecond-precision automated logging</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
              <h3 className="text-xl font-bold text-blue-400 mb-6">The Four Conditions</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {conditions.map((condition, index) => (
                  <div key={index} className={`bg-gradient-to-r ${condition.color} p-[1px] rounded-lg`}>
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h4 className="text-white font-semibold mb-2">{condition.name}</h4>
                      <div className="text-3xl font-bold text-white">{condition.accuracy}</div>
                      <div className="text-gray-400 text-sm">Memory Accuracy</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
              <h3 className="text-xl font-bold text-cyan-400 mb-6">Task Structure</h3>
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1 bg-gray-900/50 rounded-lg p-6">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mb-4">
                    <span className="text-purple-400 font-bold text-xl">1</span>
                  </div>
                  <h4 className="text-white font-semibold mb-2">Selection Phase</h4>
                  <p className="text-gray-400 text-sm">Participants select 7 objects from a grid of 21 in a specific sequence</p>
                </div>
                <div className="flex-1 bg-gray-900/50 rounded-lg p-6">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mb-4">
                    <span className="text-blue-400 font-bold text-xl">2</span>
                  </div>
                  <h4 className="text-white font-semibold mb-2">Distraction Phase</h4>
                  <p className="text-gray-400 text-sm">30-second counting task to prevent rehearsal</p>
                </div>
                <div className="flex-1 bg-gray-900/50 rounded-lg p-6">
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center mb-4">
                    <span className="text-cyan-400 font-bold text-xl">3</span>
                  </div>
                  <h4 className="text-white font-semibold mb-2">Recall Phase</h4>
                  <p className="text-gray-400 text-sm">Recreate the sequence from memory in the same spatial environment</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'findings' && (
          <div className="space-y-12">
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-green-400 mb-6">Key Finding: Landmark Interference</h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Contrary to expectations, landmarks created <strong className="text-white">cognitive interference</strong> rather 
                than spatial support. Participants showed significantly higher accuracy in landmark-absent conditions 
                (85.7%) compared to landmark-present conditions (75.3%).
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-900/50 rounded-lg p-6 text-center">
                  <div className="text-4xl font-bold text-red-400 mb-2">75.3%</div>
                  <div className="text-gray-400">With Landmarks</div>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-6 text-center">
                  <div className="text-4xl font-bold text-green-400 mb-2">85.7%</div>
                  <div className="text-gray-400">Without Landmarks</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
              <h3 className="text-xl font-bold text-blue-400 mb-6">Wall Type Equivalence</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Virtual and real wall conditions demonstrated equivalent performance, suggesting that virtual 
                boundaries provide comparable spatial reference support to physical boundaries. This validates 
                the use of virtual architectural elements in XR applications.
              </p>
              <div className="bg-blue-900/20 border-l-4 border-blue-400 pl-6 py-4">
                <p className="text-blue-200">
                  Virtual walls achieved 76.8% accuracy vs. Real walls at 84.1% — a difference that was 
                  not statistically significant when controlling for landmark effects.
                </p>
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
              <h3 className="text-xl font-bold text-purple-400 mb-6">Optimal Configuration</h3>
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-[2px] rounded-xl">
                <div className="bg-gray-900 rounded-xl p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-2xl font-bold text-white">Real Walls + No Landmarks</h4>
                      <p className="text-green-400">Best performing condition</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-gray-300">
                    <li>• 89.3% memory accuracy</li>
                    <li>• High presence ratings maintained</li>
                    <li>• Moderate cognitive load</li>
                    <li>• Fastest response times</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'implications' && (
          <div className="space-y-12">
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-purple-400 mb-6">Design Implications</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-900/50 rounded-lg p-6">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h4 className="text-white font-semibold mb-2">Minimize Visual Clutter</h4>
                  <p className="text-gray-400 text-sm">Avoid adding decorative landmarks in XR environments where spatial memory tasks are important</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-6">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h4 className="text-white font-semibold mb-2">Virtual Walls Work</h4>
                  <p className="text-gray-400 text-sm">Virtual architectural boundaries can effectively replace physical ones for spatial reference</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-6">
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <h4 className="text-white font-semibold mb-2">Task-Specific Design</h4>
                  <p className="text-gray-400 text-sm">Consider the cognitive demands of your application when designing mixed reality spaces</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-6">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h4 className="text-white font-semibold mb-2">Leverage Passthrough</h4>
                  <p className="text-gray-400 text-sm">Quest 3's passthrough enables effective integration of real environmental features</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
              <h3 className="text-xl font-bold text-blue-400 mb-6">Future Directions</h3>
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-400 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span>Investigate landmark placement strategies that might enhance rather than interfere with spatial cognition</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-400 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span>Explore how these findings apply to navigation tasks versus object memory tasks</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-400 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span>Test with larger sample sizes and diverse participant populations</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-400 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span>Examine long-term memory effects and learning curves across extended XR sessions</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Research Info */}
        <div className="mt-16 bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
          <h3 className="text-xl font-bold text-purple-400 mb-6">Research Details</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-gray-400 text-sm mb-1">Supervisor</p>
              <p className="text-white font-medium">Dr. Jay Henderson</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm mb-1">Institution</p>
              <p className="text-white font-medium">Memorial University of Newfoundland</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm mb-1">Course</p>
              <p className="text-white font-medium">COMP 499A/B - Honours Research</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm mb-1">Completion</p>
              <p className="text-white font-medium">August 2025</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/50 border-t border-gray-800 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-gray-400">
            Honours Research Thesis — Memorial University of Newfoundland
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <a
              href="https://drive.google.com/file/d/1oszfhDJw5bdDRSy5_XFAuT1kl34LArov/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 transition-colors"
            >
              Read Full Thesis →
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default XRThesisShowcase;
